/**
 * 매장 보기 배너 — 본사(전역) 관리자에게 모든 화면 맨 위에 항상 고정된다.
 *
 * 관리자는 늘 어떤 매장을 보는 상태로 둔다("본사로 나가기"·미선택 복귀는 폐지). 본진 신분(전역
 * 관리자)은 유지되지만 지금 보는·고치는 데이터는 그 매장 것이므로, 어느 매장을 만지고 있는지 늘
 * 보이게 하고 띠 안의 셀렉터로 그 자리에서 매장을 바꾼다(매장 목록은 fetchTenants — system 제외).
 *
 * owner/tenant(일반 매장 사용자)에겐 렌더되지 않는다(isGlobalAdmin 게이트 + 목록 요청도 안 함).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { fetchTenants, switchTenant } from "../api/tenants";

export default function TenantViewBanner() {
  const { isGlobalAdmin, profile } = useAuth();
  const [switching, setSwitching] = useState(false);

  const enabled = isGlobalAdmin();
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "banner"],
    queryFn: fetchTenants,
    enabled,
  });

  if (!enabled) return null;

  const currentTenantId =
    tenantsQuery.data?.currentTenantId ?? profile?.viewingTenantId ?? "";
  const tenants = tenantsQuery.data?.tenants ?? [];

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id || id === currentTenantId || switching) return;
    setSwitching(true);
    try {
      await switchTenant(id);
      // 데이터가 매장에 종속되므로 전환 후 깨끗이 리로드.
      window.location.reload();
    } catch {
      setSwitching(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm text-white shadow-sm dark:bg-amber-600">
      <div className="flex items-center gap-2 min-w-0">
        <svg
          className="h-4 w-4 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 3.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM10 7a.9.9 0 01.9.9v3.2a.9.9 0 01-1.8 0V7.9A.9.9 0 0110 7zm0 6.4a1 1 0 110 2 1 1 0 010-2z" />
        </svg>
        <span className="truncate">
          지금 보고 있는 매장 — 이 화면의 추가·수정은 해당 매장에 기록됩니다.
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="hidden text-xs font-medium opacity-90 sm:inline">
          매장 전환
        </span>
        {tenants.length > 0 ? (
          <select
            value={currentTenantId}
            onChange={handleChange}
            disabled={switching}
            aria-label="보는 매장 선택"
            className="max-w-[12rem] truncate rounded-md border border-white/30 bg-white/20 px-2.5 py-1 text-xs font-semibold text-white outline-none transition hover:bg-white/30 focus:ring-2 focus:ring-white/50 disabled:opacity-60 [&>option]:text-gray-900"
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.status !== "active" ? " (비활성)" : ""}
              </option>
            ))}
          </select>
        ) : (
          <strong className="font-semibold">
            {profile?.viewingTenantName ?? "매장"}
          </strong>
        )}
      </div>
    </div>
  );
}
